import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatEventTitle } from "@/lib/utils";
import {
  Sun,
  Sunset,
  FileText,
  Loader2,
  CheckCircle2,
  ArrowLeft,
  Sparkles,
  Flame,
  ChevronRight,
} from "lucide-react";
import { QuizModal } from "@/components/QuizModal";

const KIND_META = {
  morning_prov: { Icon: Sun, color: "#F6CA83", label: "Morgon" },
  afternoon_focus: { Icon: Sunset, color: "#7CA858", label: "Eftermiddag" },
  countdown: { Icon: FileText, color: "#82A0BC", label: "Provförberedelse" },
};

export default function StudyReminderPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const token = searchParams.get("t");
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [streaks, setStreaks] = useState({});
  const [completedExtras, setCompletedExtras] = useState({});
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizResult, setQuizResult] = useState(null);
  const [selfAssessment, setSelfAssessment] = useState(null);
  const [savingAssessment, setSavingAssessment] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Saknar token — öppna sidan via mail/push-länken");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/study-reminders/page/${id}?t=${token}`);
        setPage(data);
        setStreaks(data.streaks || {});
        // För-fyll completedExtras från completions
        const done = {};
        const today = new Date().toISOString().slice(0, 10);
        (data.completions || []).forEach((c) => {
          if (c.kind === "extra_done" && c.label && (c.at || "").slice(0, 10) === today) {
            done[c.label] = true;
          }
        });
        setCompletedExtras(done);
        // Hitta tidigare quiz-resultat
        const lastQuiz = (data.completions || [])
          .filter((c) => c.kind === "quiz_result")
          .pop();
        if (lastQuiz) setQuizResult(lastQuiz);
        const lastAssess = (data.completions || [])
          .filter((c) => c.kind === "self_assessment_after")
          .pop();
        if (lastAssess) setSelfAssessment(lastAssess.value);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Kunde inte ladda sidan");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, token]);

  const logExtraDone = async (label) => {
    if (completedExtras[label]) return;
    try {
      const { data } = await api.post(
        `/study-reminders/page/${id}/complete?t=${token}`,
        { kind: "extra_done", label }
      );
      setCompletedExtras((prev) => ({ ...prev, [label]: true }));
      if (data.streak) {
        setStreaks((prev) => ({ ...prev, [label]: data.streak.streak }));
        if (data.streak.streak >= 3) {
          toast.success(`🔥 ${data.streak.streak} dagar i rad — bra jobbat!`);
        }
      }
    } catch (e) {
      toast.error("Kunde inte logga — försök igen");
    }
  };

  const onQuizSubmit = async (result) => {
    // result från QuizModal-callback (vi använder en custom callback här)
    setQuizResult(result);
    try {
      await api.post(`/study-reminders/page/${id}/complete?t=${token}`, {
        kind: "quiz_result",
        value: result.correct,
        detail: { total: result.total, percent: result.percent },
      });
    } catch (e) {
      // soft fail
    }
  };

  const saveSelfAssessment = async (value) => {
    setSavingAssessment(true);
    try {
      await api.post(`/study-reminders/page/${id}/complete?t=${token}`, {
        kind: "self_assessment_after",
        value,
      });
      setSelfAssessment(value);
      toast.success("Tack — skattning sparad!");
    } catch (e) {
      toast.error("Kunde inte spara");
    } finally {
      setSavingAssessment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 size={24} className="animate-spin text-[color:var(--moss)]" />
      </div>
    );
  }
  if (!page) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center">
        <div>
          <p className="text-lg font-medium">Sidan kunde inte laddas</p>
          <p className="text-sm text-[color:var(--text-tertiary)] mt-2">
            Länken kan ha gått ut (14 dagar) eller token saknas.
          </p>
        </div>
      </div>
    );
  }

  const { Icon, color, label } = KIND_META[page.kind] || KIND_META.afternoon_focus;

  return (
    <div className="min-h-screen pb-16" data-testid="study-reminder-page">
      {/* Hero */}
      <div
        className="px-5 pt-5 pb-7"
        style={{ background: `linear-gradient(180deg, ${color}33, transparent)` }}
      >
        <button
          onClick={() => nav("/")}
          className="inline-flex items-center gap-1 text-xs text-[color:var(--text-secondary)] mb-3"
          data-testid="study-back"
        >
          <ArrowLeft size={12} /> Hem
        </button>
        <div className="flex items-center gap-2 mb-2">
          <Icon size={16} style={{ color }} />
          <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color }}>
            {label}
          </p>
        </div>
        <h1 className="font-display text-2xl font-medium leading-tight">
          {page.title}
        </h1>
        <p className="text-sm text-[color:var(--text-secondary)] mt-2 leading-relaxed">
          {page.intro}
        </p>
      </div>

      {/* Events */}
      {page.event_refs?.length > 0 && (
        <div className="px-5 mt-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-2">
            {page.kind === "morning_prov" ? "Provet" : "Att fokusera på"}
          </p>
          <div className="space-y-2">
            {page.event_refs.map((e) => (
              <div
                key={e.id}
                className="card-soft p-3"
                data-testid={`study-event-${e.id}`}
              >
                <p className="text-sm font-medium leading-snug">{formatEventTitle(e)}</p>
                {e.vklass_description && (
                  <p className="text-xs text-[color:var(--text-secondary)] mt-1 leading-relaxed line-clamp-3">
                    {e.vklass_description}
                  </p>
                )}
                <p className="text-[11px] text-[color:var(--text-tertiary)] mt-1">
                  {(e.start || "").slice(0, 10)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Afternoon: extras */}
      {page.kind === "afternoon_focus" && page.extras?.length > 0 && (
        <div className="px-5 mt-5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-2">
            Extra ämnen idag
          </p>
          <div className="space-y-2">
            {page.extras.map((ex) => {
              const done = !!completedExtras[ex.label];
              const streak = streaks[ex.label] || 0;
              return (
                <button
                  key={ex.label}
                  onClick={() => logExtraDone(ex.label)}
                  disabled={done}
                  className={`w-full text-left card-soft p-3 flex items-center gap-3 transition-colors ${
                    done ? "opacity-70" : "hover:border-[color:var(--moss)]/40"
                  }`}
                  data-testid={`study-extra-${ex.label}`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      done
                        ? "bg-[color:var(--moss)] text-white"
                        : "bg-[color:var(--bg-elevated)] text-[color:var(--text-secondary)]"
                    }`}
                  >
                    {done ? <CheckCircle2 size={16} /> : <ChevronRight size={14} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{ex.minutes} min {ex.label}</p>
                    {streak > 0 && (
                      <p className="text-[11px] text-[color:var(--text-tertiary)] flex items-center gap-1 mt-0.5">
                        <Flame size={10} className="text-orange-500" />
                        {streak} dag{streak === 1 ? "" : "ar"} i rad
                      </p>
                    )}
                  </div>
                  {done && (
                    <span className="text-[10px] font-medium text-[color:var(--moss)]">
                      Klart
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Countdown: quiz + self-assessment */}
      {page.kind === "countdown" && page.quiz_event_id && (
        <div className="px-5 mt-5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-2">
            Snabb-quiz (10 frågor)
          </p>
          {!quizResult ? (
            <button
              onClick={() => setShowQuiz(true)}
              className="w-full card-soft p-4 flex items-center gap-3 hover:border-[color:var(--moss)]/40"
              data-testid="study-start-quiz"
            >
              <Sparkles size={18} className="text-[color:var(--jarvis)]" />
              <div className="flex-1 text-left">
                <p className="text-sm font-medium">Starta quiz</p>
                <p className="text-xs text-[color:var(--text-tertiary)]">
                  10 frågor om {formatEventTitle(page.event_refs[0]) || "provet"}
                </p>
              </div>
              <ChevronRight size={16} className="text-[color:var(--text-tertiary)]" />
            </button>
          ) : (
            <div className="card-soft p-4">
              <p className="text-sm font-medium">
                Du fick {quizResult.value || quizResult.correct} av{" "}
                {quizResult.detail?.total || quizResult.total || 10} rätt
              </p>
              <p className="text-xs text-[color:var(--text-tertiary)] mt-1">
                {(quizResult.detail?.percent || quizResult.percent || 0) >= 80
                  ? "Strålande — du sitter med detta!"
                  : (quizResult.detail?.percent || quizResult.percent || 0) >= 50
                  ? "Bra — repetera de svåra delarna."
                  : "Plugga lite till på det du missade."}
              </p>
              <button
                onClick={() => {
                  setQuizResult(null);
                  setShowQuiz(true);
                }}
                className="mt-3 text-xs text-[color:var(--moss)] font-medium hover:underline"
                data-testid="study-redo-quiz"
              >
                Gör om quizen
              </button>
            </div>
          )}

          {/* Self-assessment EFTER quiz */}
          {quizResult && (
            <div className="mt-5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-2">
                Hur säker känner du dig nu? (1 = osäker, 10 = supersäker)
              </p>
              <div className="grid grid-cols-10 gap-1">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
                  const active = selfAssessment === n;
                  return (
                    <button
                      key={n}
                      onClick={() => saveSelfAssessment(n)}
                      disabled={savingAssessment}
                      className={`aspect-square rounded-lg text-sm font-medium border transition-colors ${
                        active
                          ? "bg-[color:var(--moss)] text-white border-[color:var(--moss)]"
                          : "bg-white text-[color:var(--text-secondary)] border-[color:var(--border)] hover:border-[color:var(--moss)]/40"
                      }`}
                      data-testid={`study-assess-${n}`}
                    >
                      {n}
                    </button>
                  );
                })}
              </div>
              {selfAssessment && (
                <p className="text-xs text-[color:var(--text-tertiary)] mt-3 leading-relaxed">
                  {selfAssessment >= 8
                    ? "Bra! Du verkar redo. Repetera bara det viktigaste innan provet."
                    : selfAssessment >= 5
                    ? "Helt OK. Lägg en stund till på det som kändes svårast."
                    : "Lugn — du har tid. Be om hjälp om något inte sitter."}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Quiz modal — wraps existing component */}
      {showQuiz && page.quiz_event_id && (
        <QuizModal
          eventId={page.quiz_event_id}
          onClose={() => setShowQuiz(false)}
          onCompleted={(result) => {
            onQuizSubmit(result);
            setShowQuiz(false);
          }}
        />
      )}
    </div>
  );
}
